import { useCompanyContext } from '@/hooks/use-company-context';
import { useGpsNotifications } from '@/hooks/useGpsNotifications';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Bell, RefreshCw, CheckCircle, XCircle, Clock, RotateCw, Info, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';

export default function GpsNotificationsPage() {
  const { companyId } = useCompanyContext();
  const { notifications, isLoading, retryNotification, stats } = useGpsNotifications(companyId);

  // Get slug from URL for redirect
  const pathSegments = window.location.pathname.split('/');
  const slug = pathSegments[2]; // /company/{slug}/...

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      success: 'default',
      failed: 'destructive',
      pending: 'secondary',
    };

    const labels: Record<string, string> = {
      success: 'Enviado',
      failed: 'Falhou',
      pending: 'Pendente',
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      webhook: 'Webhook',
      email: 'E-mail',
      telegram: 'Telegram',
    };
    return labels[type] || type;
  };

  return (
    <GpsLicenseGate>
      <div className="space-y-6">
        {/* Redirection Banner */}
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/20">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900 dark:text-blue-100">Esta funcionalidade foi movida</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span className="text-blue-800 dark:text-blue-200">
              Notificações GPS agora fazem parte do <strong>FleetONE integrado</strong>.
              Acesse através do menu FleetONE para visualizar todas as notificações unificadas.
            </span>
            <Button variant="default" size="sm" asChild>
              <Link to={`/company/${slug}/admin/systems/fleet/notifications`}>
                <ArrowRight className="h-4 w-4 mr-2" />
                Ir para Notificações FleetONE
              </Link>
            </Button>
          </AlertDescription>
        </Alert>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Bell className="h-8 w-8" />
              Notificações GPS
            </h1>
            <p className="text-muted-foreground mt-1">
              Histórico de notificações enviadas pelo sistema
            </p>
          </div>
          <Button variant="outline" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Total</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Enviadas</div>
            <div className="text-2xl font-bold text-green-600">{stats.success}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Pendentes</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Falhas</div>
            <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
          </Card>
        </div>

        {/* Notifications List */}
        <Card className="p-4">
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Carregando notificações...</p>
              </div>
            ) : notifications?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma notificação registrada</p>
              </div>
            ) : (
              notifications?.map((notification) => (
                <Card key={notification.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(notification.status)}
                      <div>
                        <h3 className="font-semibold">{notification.event_type}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getTypeLabel(notification.type)}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(notification.status)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span className="text-muted-foreground">Criado:</span>{' '}
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </div>
                    {notification.delivered_at && (
                      <div>
                        <span className="text-muted-foreground">Entregue:</span>{' '}
                        {formatDistanceToNow(new Date(notification.delivered_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Tentativas:</span> {notification.retries}
                    </div>
                  </div>

                  {notification.error_message && (
                    <div className="bg-destructive/10 text-destructive text-sm p-2 rounded mb-3">
                      {notification.error_message}
                    </div>
                  )}

                  {notification.status === 'failed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => retryNotification.mutate(notification.id)}
                      disabled={retryNotification.isPending}
                    >
                      <RotateCw className="h-4 w-4 mr-2" />
                      Tentar Novamente
                    </Button>
                  )}
                </Card>
              ))
            )}
          </div>
        </Card>
      </div>
    </GpsLicenseGate>
  );
}
